import gql from 'graphql-tag';
import apolloClient from '../apollo';
import jwt from 'jsonwebtoken';
import { getAuthToken } from '../helpers/localStorage';
import store from '@/store';

/**
 * State
 */
const getDefaultState = () => {
  return {
    user: {},
    token: null,
    tokenExpiration: null,
    isFetching: false,
    errorMessage: '',
  };
};
const state = getDefaultState();

/**
 * Defines the fields which are requested for a user
 * used for all queries/mutations which return a user
 * @type {DocumentNode}
 */
const USER_REQUEST_FIELDS = gql`
  fragment userFields on User {
    id
    number
    firstname
    lastname
    email
    role
    createdAt
    position
    company
    projectExportBuildState
    projectExportDetailedBuildState
    projectOfferExportBuildState
    orderExportBuildState
    timeTrackingExportBuildState
    appointmentExportBuildState
    employeeUtilizationExportBuildState
    ordersExportBuildState
    itemInventoriesExportBuildState
    authorizationAttributes {
      role
      department
    }
    employer {
      id
      number
      name
      street
      zip
      city
      type
      electricians {
        name
        id
        number
      }
    }
    client {
      id
      number
      name
      street
      zip
      city
      type
      electricians {
        name
        id
        number
      }
      employers {
        name
        id
        number
      }
    }
    clients {
      id
      number
      name
      street
      zip
      city
      type
      roleNavigationEnabled
      electricians {
        name
        id
        number
      }
      employers {
        name
        id
        number
      }
    }
    avatar {
      id
      path
    }
  }
`;

/**
 * Actions
 */
const actions = {
  /**
   * Reset state in module
   * @param commit
   */
  reset({ commit }) {
    commit('SET_DEFAULT_STATE');
  },

  /**
   * Send credentials to GraphQL API and receive a json web token to authenticate further requests
   * @param commit
   * @param logindata
   * @returns {Promise<void>}
   */
  async graphqlLogin({ dispatch, commit }, logindata) {
    // Login process:
    // - send credentials to backend
    // - save token + user data to user object
    // - protect routes, check user object
    // - show error message on false login
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation login($input: LoginInput!) {
            login(loginInput: $input) {
              user {
                ...userFields
              }
              token
              tokenExpiration
            }
          }
          ${USER_REQUEST_FIELDS}
        `,
        variables: {
          input: {
            email: logindata.email,
            password: logindata.password,
          },
        },
      });
      if (response.errors) {
        // console.log(response.errors)
        dispatch('loginError', response.errors[0].message);
      } else {
        // console.log(response.data.login);
        commit('SET_USER', response.data.login);
        dispatch('updateLocalStorage', response.data.login);
        this.dispatch('addOrUpdateCurrentUser', response.data.login.user);
        commit('LOGIN_SUCCESS');

        // Reset policies loaded flag for new user login
        commit('policies/SET_POLICIES_LOADED', false, { root: true });

        // Load user policies after successful login
        console.log('graphqlLogin() - Loading user policies');
        dispatch('policies/fetchUserPolicies', null, { root: true });
      }
    } catch (err) {
      // console.log(err)
      dispatch('loginError', err);
      // commit('SET_FLASH_MESSAGE', {message: err.message, type: 'is-danger'})
    }
  },

  /**
   *
   * @param dispatch
   * @param commit
   * @returns {Promise<void>}
   */
  async refreshUserLegacy({ dispatch, commit }) {
    const currentUser = store.getters['auth/getCurrentUser'];
    if (!currentUser.id) {
      return;
    }

    try {
      const response = await apolloClient.query({
        query: gql`
          query user($userId: ID!) {
            user(userId: $userId) {
              ...userFields
            }
          }
          ${USER_REQUEST_FIELDS}
        `,
        variables: {
          userId: currentUser.id,
        },
      });
      commit('SET_USER', response.data.user);
      this.dispatch('addOrUpdateCurrentUser', response.data.user);
    } catch (e) {
      // console.log(e)
    }
  },

  /**
   * Update user data and JWT Token from server
   * @param {*} param0
   * @returns
   */
  async refreshUser({ dispatch, commit }) {
    const currentUser = store.getters['auth/getCurrentUser'];
    // console.log('refreshUser()', currentUser);

    if (!currentUser.id) {
      return;
    }

    try {
      const response = await apolloClient.query({
        query: gql`
          query userAndToken($userId: ID!) {
            userAndToken(userId: $userId) {
              user {
                ...userFields
              }
              token
              tokenExpiration
            }
          }
          ${USER_REQUEST_FIELDS}
        `,
        variables: {
          userId: currentUser.id,
        },
      });
      // console.log('refreshUser()', response.data.userAndToken);
      commit('SET_USER', response.data.userAndToken);
      dispatch('updateLocalStorage', response.data.userAndToken);
      this.dispatch('addOrUpdateCurrentUser', response.data.userAndToken.user);
      console.log('refreshUser() - Loading user policies');
      dispatch('policies/fetchUserPolicies', null, { root: true });
    } catch (e) {
      console.log('refreshUser() - error', e);
    }
  },

  /**
   * If a JWT is saved in localStorage and not expired log in with that token
   * @param commit
   * @returns {Promise<void>}
   */
  loginFromStorage({ commit, dispatch }) {
    const localUser = JSON.parse(localStorage.getItem('user'));
    if (localUser) {
      // check expiration
      // get expiration from jwt and convert to milliseconds
      const token = getAuthToken();
      if (!token) {
        return false;
      }
      const expiration = jwt.decode(token).exp * 1000;

      // const now = Date.now() + (1000 * 60 * 60)
      const now = Date.now();
      // console.log(now, expiration, (expiration - now) / 1000 / 60 / 60);
      // console.log(`Now: ${new Date(now).toLocaleDateString()} ${new Date(now).toLocaleTimeString()}`)
      // console.log(`Expires: ${new Date(expiration).toLocaleDateString()} ${new Date(expiration).toLocaleTimeString()}`)
      if (now > expiration) {
        // console.log(`Token expired on ${new Date(expiration).toLocaleDateString()} ${new Date(expiration).toLocaleTimeString()}`)
        dispatch('logoutUser');
        return false;
      }
      // console.log('Logging in from local storage: ' + localUser.email)
      commit('SET_USER', {
        token: getAuthToken(),
        tokenExpiration: localStorage.getItem('tokenExpiration'),
        user: localUser,
      });

      // Load user policies from localStorage if available
      const userPolicies = JSON.parse(localStorage.getItem('userPolicies'));
      if (userPolicies) {
        // Set the policies from localStorage directly
        commit('policies/SET_USER_POLICIES', userPolicies, { root: true });
        commit('policies/SET_POLICIES_LOADED', true, { root: true });
        console.log('Loaded user policies from localStorage');
      } else {
        // Reset flag before loading to ensure policies are fetched
        commit('policies/SET_POLICIES_LOADED', false, { root: true });
        // Fall back to async loading if not in localStorage
        console.log('loginFromStorage() - Loading user policies asynchronously');
        dispatch('policies/fetchUserPolicies', null, { root: true });
      }
    }
  },

  /**
   * request an async build of a project by type
   * @param {*} param0
   * @param {*} projectId
   */
  async buildExportByType({ commit, dispatch }, { userNumber, exportType }) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation buildExportByType($userNumber: Int!, $exportType: ExportType!) {
            buildExportByType(userNumber: $userNumber, exportType: $exportType)
          }
        `,
        variables: {
          userNumber: userNumber,
          exportType: exportType,
        },
      });
      commit('UPDATE_APPOINTMENT_EXPORT_BUILD_STATE', {
        appointmentExportBuildState: response.data.buildExportByType,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * request an async build of a project export
   * @param {*} param0
   * @param {*} projectId
   */
  async buildUserProjectExport({ commit, dispatch }, userId) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation buildProjectExport($userId: ID!) {
            buildProjectExport(userId: $userId)
          }
        `,
        variables: {
          userId: userId,
        },
      });
      commit('UPDATE_PROJECT_EXPORT_BUILD_STATE', {
        projectExportBuildState: response.data.buildProjectExport,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },
  /**
   * Request an async build of a time tracking export,
   * now with export parameters (page, pageSize, sortField, sortOrder, filters).
   * @param {*} context
   * @param {*} payload - contains userId, page, pageSize, sortField, sortOrder, filters
   */
  async buildUserTimeTrackingExport(
    { commit, dispatch },
    { userId, page, pageSize, sortField, sortOrder, filters }
  ) {
    if (!userId) {
      throw new Error('UserId is undefined or null');
    }

    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation buildTimeTrackingExport(
            $userId: ID!
            $page: Int!
            $pageSize: Int!
            $sortField: String
            $sortOrder: Int
            $filters: TimeTrackingFilterInput
          ) {
            buildTimeTrackingExport(
              userId: $userId
              page: $page
              pageSize: $pageSize
              sortField: $sortField
              sortOrder: $sortOrder
              filters: $filters
            )
          }
        `,
        variables: {
          userId,
          page,
          pageSize,
          sortField,
          sortOrder,
          filters,
        },
      });
      commit('UPDATE_TIMETRACKING_EXPORT_BUILD_STATE', {
        timeTrackingExportBuildState: response.data.buildTimeTrackingExport,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Erstellen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },
  /**
   * Request an async build of an employee utilization export
   * @param {*} context
   * @param {*} payload - contains userId, pagination, sorting, filters
   */
  async buildUserEmployeeUtilizationExport(
    { commit, dispatch },
    { userId, pagination, sorting, filters }
  ) {
    if (!userId) {
      throw new Error('UserId is undefined or null');
    }

    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation buildEmployeeUtilizationExport(
            $userId: ID!
            $pagination: PaginationInput!
            $sorting: SortingInput
            $filters: EmployeeUtilizationFilterInput
          ) {
            buildEmployeeUtilizationExport(
              userId: $userId
              pagination: $pagination
              sorting: $sorting
              filters: $filters
            )
          }
        `,
        variables: {
          userId,
          pagination: {
            skip: pagination.page * pagination.pageSize,
            pageSize: pagination.pageSize,
          },
          sorting,
          filters,
        },
      });
      commit('UPDATE_EMPLOYEE_UTILIZATION_EXPORT_BUILD_STATE', {
        employeeUtilizationExportBuildState: response.data.buildEmployeeUtilizationExport,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Erstellen der Auslastungsübersicht!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },
  /**
   * request an async build of a order export
   * @param {*} param0
   * @param {*} projectId
   */
  async buildUserOrderExport({ commit, dispatch }, { userId, projectNumber }) {
    if (typeof projectNumber === 'undefined' || projectNumber === null) {
      throw new Error('Project Number is undefined or null');
    }

    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation buildOrderExport($userId: ID!, $projectNumber: Int!) {
            buildOrderExport(userId: $userId, projectNumber: $projectNumber)
          }
        `,
        variables: {
          userId: userId,
          projectNumber: projectNumber,
        },
      });
      commit('UPDATE_ORDER_EXPORT_BUILD_STATE', {
        orderExportBuildState: response.data.buildOrderExport,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Erstellen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },
  /**
   * request an async build of a detailed project export
   * @param {*} param0
   * @param {*} userId
   */
  async buildUserProjectExportDetailed({ commit, dispatch }, userId) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation buildProjectExportDetailed($userId: ID!) {
            buildProjectExportDetailed(userId: $userId)
          }
        `,
        variables: {
          userId: userId,
        },
      });
      commit('UPDATE_PROJECT_EXPORT_DETAILED_BUILD_STATE', {
        projectExportDetailedBuildState: response.data.buildProjectExportDetailed,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },
  /**
   * request an async build of a project offer export
   * @param {*} param0
   * @param {*} projectId
   */
  async buildUserProjectOfferExport({ commit, dispatch }, userId) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation buildProjectOfferExport($userId: ID!) {
            buildProjectOfferExport(userId: $userId)
          }
        `,
        variables: {
          userId: userId,
        },
      });
      commit('UPDATE_PROJECT_OFFER_EXPORT_BUILD_STATE', {
        projectExportBuildState: response.data.buildProjectOfferExport,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * Save Token and Userdata to local storage
   * @param dispatch
   * @param token
   */
  updateLocalStorage({ dispatch }, { token, user }) {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));

    // Get and store policies in localStorage when updating credentials
    dispatch('policies/fetchUserPolicies', null, { root: true }).then((policies) => {
      if (policies) {
        localStorage.setItem('userPolicies', JSON.stringify(policies));
      }
    });
  },

  /**
   *
   * @param dispatch
   */
  async logoutUser({ commit, dispatch }) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userPolicies');
    document.cookie = 'token=;expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    // axios.defaults.headers.common['Authorization'] = "";
    commit('SET_DEFAULT_STATE');
    await dispatch('resetMyAccess', null, { root: true });
    // await router.push('/login');
  },
  loginError({ commit }, payload) {
    commit('LOGIN_FAILURE', payload);
  },
  // receiveLogin({ commit }) {
  //   commit('LOGIN_SUCCESS');
  //   router.push('/app/users');
  // },
  requestLogin({ commit }) {
    commit('LOGIN_REQUEST');
  },
};

/**
 * Mutations
 */
const mutations = {
  SET_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  LOGIN_FAILURE(state, payload) {
    state.isFetching = false;
    state.errorMessage = payload;
  },
  LOGIN_SUCCESS(state) {
    state.isFetching = false;
    state.errorMessage = '';
  },
  LOGIN_REQUEST(state) {
    state.isFetching = true;
  },
  SET_USER: (state, auth) => {
    Object.assign(state, auth);
    // localStorage.setItem('USER', JSON.stringify(state.user))
  },
  UPDATE_PROJECT_EXPORT_BUILD_STATE: (state, { projectExportBuildState }) => {
    state.user.projectExportBuildState = projectExportBuildState;
  },
  UPDATE_PROJECT_EXPORT_DETAILED_BUILD_STATE: (state, { projectExportDetailedBuildState }) => {
    state.user.projectExportDetailedBuildState = projectExportDetailedBuildState;
  },
  UPDATE_ORDER_EXPORT_BUILD_STATE: (state, { orderExportBuildState }) => {
    state.user.orderExportBuildState = orderExportBuildState;
  },
  UPDATE_TIMETRACKING_EXPORT_BUILD_STATE: (state, { timeTrackingExportBuildState }) => {
    state.user.timeTrackingExportBuildState = timeTrackingExportBuildState;
  },
  UPDATE_PROJECT_OFFER_EXPORT_BUILD_STATE: (state, { projectOfferExportBuildState }) => {
    state.user.projectOfferExportBuildState = projectOfferExportBuildState;
  },
  UPDATE_APPOINTMENT_EXPORT_BUILD_STATE: (state, { appointmentExportBuildState }) => {
    state.user.appointmentExportBuildState = appointmentExportBuildState;
  },
  UPDATE_EMPLOYEE_UTILIZATION_EXPORT_BUILD_STATE: (
    state,
    { employeeUtilizationExportBuildState }
  ) => {
    state.user.employeeUtilizationExportBuildState = employeeUtilizationExportBuildState;
  },
};

/**
 * Getters
 */
const getters = {
  isAuth: (state) => (state.user.token ? true : false),
  getAuthToken: (state) => localStorage.getItem('token'),
  isAdmin: (state) => state.user.role === 'ADMIN',
  /** The organization of a user is the first client, as in the backend (OrganizationService). */
  isOrganizationRoleNavigationEnabled: (state) =>
    state.user.clients?.[0]?.roleNavigationEnabled === true,
  isEmployer: (state) => state.user.role === 'EMPLOYER',
  isClient: (state) => state.user.role === 'CLIENT',
  isGuest: (state) => state.user.role === 'GUEST',
  isSalesLead: (state) =>
    state.user.authorizationAttributes?.role === 'TEAM_LEAD' &&
    state.user.authorizationAttributes?.department === 'SALES',
  isSales: (state) =>
    state.user.authorizationAttributes?.role === 'SALES' &&
    state.user.authorizationAttributes?.department === 'SALES',
  isController: (state) =>
    state.user.authorizationAttributes?.role === 'MANAGER' &&
    state.user.authorizationAttributes?.department === 'CONTROLLING',
  isStandardControlling: (state) =>
    state.user.authorizationAttributes?.role === 'ROLE_STANDARD' &&
    state.user.authorizationAttributes?.department === 'CONTROLLING',
  isManagement: (state) =>
    state.user.authorizationAttributes?.role === 'MANAGER' &&
    state.user.authorizationAttributes?.department === 'PROJECT_LEAD',
  getCurrentUser: (state) => state.user,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
